import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/* No-login wishlist: product slugs persisted in the shopper's browser
   (localStorage). Survives reloads; independent per device by design. */
const STORAGE_KEY = "levami_wishlist";

const WishlistContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

export function WishlistProvider({ children }) {
  const [slugs, setSlugs] = useState(readStored);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slugs));
    } catch {
      /* storage unavailable (private mode) — wishlist stays in-memory */
    }
  }, [slugs]);

  const has = useCallback((slug) => slugs.includes(slug), [slugs]);

  /* Anonymous aggregate ping so the admin dashboard can show the most-saved
     products. Fire-and-forget: wishlist UX never waits on (or breaks from) it. */
  const track = (slug, action) => {
    api.post("/wishlist/track", { slug, action }).catch(() => {});
  };

  const toggle = useCallback((slug) => {
    if (!slug) return;
    // Side effect kept OUTSIDE the state updater: React StrictMode double-invokes
    // updater functions, which would otherwise double-count every toggle.
    const adding = !slugs.includes(slug);
    track(slug, adding ? "add" : "remove");
    setSlugs((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  }, [slugs]);

  const remove = useCallback((slug) => {
    if (slugs.includes(slug)) track(slug, "remove");
    setSlugs((prev) => prev.filter((s) => s !== slug));
  }, [slugs]);

  return (
    <WishlistContext.Provider value={{ slugs, count: slugs.length, has, toggle, remove }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
}
